import { UsingDrawIndirect, UsingMultiDraw } from "../globals";
import { ModelIteratorLinear } from "../../scene/ModelIteratorLinear.js";
import { MeshFlags } from "../../scene/MeshFlags.js";
import { getDefaultPipelineHash } from "../Pipelines.js";
import { MultiDrawBatch } from "./MultiDrawBatch.js";

/** @import { RenderBatch } from "../../scene/RenderBatch" */
/** @import { RenderBatchUniforms } from "../main/uniforms/RenderBatchUniforms" */
/** @import { GPUCullingEngine } from './GPUCullingEngine' */
/** @import { Renderer } from "../Renderer" */

/**
 * Main API for GPU-driven rendering of scene nodes
 *
 * class @GPUSceneRenderer
 */
export class GPUSceneRenderer {

  /**
   * Returns true if indirect draw is enabled and supported by the device (either multiDraw or single drawIndirect calls)
   */
  static indirectDrawEnabled() {
    return UsingMultiDraw() || UsingDrawIndirect();
  }

  /**
   * Ensures that a RenderBAtch is prepared for multidraw rendering (if possible).
   * On success, multiDraw data is attached to the RenderBatch (RenderBatch.__gpuMultiDrawBatch)
   * Note that this modifies the RenderBatch by resorting and attaching __gpuMultiDrawBatch.
   *
   * Requirements:
   *  - All geometry must be loaded and uploaded to GPU
   *  - All material must be avaible
   *
   * We use renderBatch.isComplete to check this. Note that individual persistently missing or broken geometries or materials are tolerated.
   * However, if renderBatch.isComplete is true, we assume that any still missing geometry or material will keep missing and dferring the call
   * wo9uldn't make any diffference.
   *
   * Note: Batches containing point geometry are not multi-drawable and must be
   * filtered out by the caller (see GPUSceneRenderer.containsPointGeometry) before calling this.
   *
   * @param {RenderBatch} renderBatch
   * @param {number} activeFragCount - Count of frags with geometries and materials
   * @param {Renderer} renderer
   * @returns {boolean} true if RenderBatch is ready to render with MultiDraws
   **/
  static tryInitScene(renderBatch, activeFragCount, renderer) {
    // Nothing to do if multiDraw data is already available
    if (GPUSceneRenderer.canUseIndirectDraw(renderBatch)) {
      return true;
    }

    const renderBatchUniforms = renderer.getRenderBatchUniforms();
    // Check if the RenderBatch is ready to be optimized into MultiDraws
    if (!isReadyToOptimize(renderBatch, renderBatchUniforms)) {
      return false;
    }

    // Sort fragments for efficient rendering
    const span = sortForMultiDraw(renderBatch);

    // Create MultiDrawBatch from sorted fragments
    renderBatch.__gpuMultiDrawBatch = MultiDrawBatch.fromFragments(
      renderer, renderBatch.frags, activeFragCount, span, renderBatch);

    // Due to the re-sorting of the fragments, we have to update the uniforms to match the order.
    // TODO: This is quite a waste - finally should make sure that the uniforms are only allocated first,
    //       but not uploaded before sorting is done.
    renderBatch.uniformsNeedUpdate = true;

    return true;
  }

  /**
   * Returns true if any fragment in the RenderBatch uses point geometry.
   * Point geometry is rendered via a dedicated SSBO path (see VertexBuffer.js) and is not
   * compatible with multi-draw batching: cull-instances.wgsl assumes a continuous range of
   * non-point fragments, so a batch containing points must fall back to single-draw rendering.
   * @param {RenderBatch} renderBatch
   * @returns {boolean}
   */
  static containsPointGeometry(renderBatch) {
    const frags = renderBatch.frags;
    const indices = renderBatch.getIndices();
    // lastItem is the exclusive range end (see RenderBatch).
    for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {
      if (frags.getGeometry(indices[i])?.isPoints) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the RenderBatch already has all data ready to allow indirect rendering
   * @param {RenderBatch} renderBatch
   * @returns {boolean}
   */
  static canUseIndirectDraw(renderBatch) {
    const enabled = GPUSceneRenderer.indirectDrawEnabled();
    return enabled && Boolean(renderBatch.__gpuMultiDrawBatch);
  }

  /**
   * Get RenderBatch's MultiDrawBatch if one exists.
   * @param {RenderBatch} renderBatch
   * @returns {MultiDrawBatch|null}
   */
  static maybeGetMultiDrawBatch(renderBatch) {
    if (GPUSceneRenderer.indirectDrawEnabled() &&
    renderBatch.__gpuMultiDrawBatch) {
      return renderBatch.__gpuMultiDrawBatch;
    } else {
      return null;
    }
  }

  /**
   * @param {RenderBatch} renderBatch
   * @param {Renderer} renderer
   */
  static clearMultiDrawBatch(renderBatch, renderer) {
    // Clear the MultiDrawBatch to free GPU resources
    if (renderBatch.__gpuMultiDrawBatch) {
      renderBatch.__gpuMultiDrawBatch.destroy(renderer);
      renderBatch.__gpuMultiDrawBatch = null;
    }
  }
}

/**
 * Sort fragments in a way that is optimized for rendering with MultiDrawIndirect.
 * Todo: Consider unifiying this with the sorting functions in RenderBatch.
 * @param {RenderBatch} renderBatch
 * @returns {Uint32Array} Subarray of renderBatch.indices pointing to the sorted fragmentIds in the RenderBatch. Note that RenderBatch.indices may contain more than the RenderBatch itself.
 */
export function sortForMultiDraw(renderBatch) {

  const frags = renderBatch.frags;

  // Note that the RenderBatch() only includes a range within renderBatch.getIndices().
  // So, we need a subarray to restrict sorting to that range.
  const indices = renderBatch.getIndices();
  let span = indices.subarray(renderBatch.start, renderBatch.lastItem);

  // Define sort order in a way that we...
  //  - Fragments that can be merged into a single multiDrawCall form a group
  //  - Within each group, instances of the same geometry are behind each other.
  const sortBy = (fragIdA, fragIdB) => {

    const geomA = frags.getGeometry(fragIdA);
    const geomB = frags.getGeometry(fragIdB);
    const materialA = frags.getMaterial(fragIdA);
    const materialB = frags.getMaterial(fragIdB);

    const aActive = materialA && geomA;
    const bActive = materialB && geomB;
    // All inactive frags must be at the end. This ensures that mapping an
    // instanceIndex to an instanceId can always be done by just adding the
    // first instanceId in the batch.
    if (!aActive) {
      return 1;
    } else if (!bActive) {
      return -1;
    }

    // Criterion 1: PipelineHash
    // TODO: Consider caching the hash instead of computing it every time
    const pipeHashA = getDefaultPipelineHash(geomA, materialA);
    const pipeHashB = getDefaultPipelineHash(geomB, materialB);

    // Group by pipeline hash as first criterion
    if (pipeHashA < pipeHashB) {
      return -1;
    } else if (pipeHashA > pipeHashB) {
      return 1;
    }

    // Criterion 2: VertexBuffer (ascending id order)
    // get GPUBuffers that contain the geometries
    const vbIda = geomA ? geomA.__gpuvb?.id : Number.MAX_SAFE_INTEGER;
    const vbIdb = geomB ? geomB.__gpuvb?.id : Number.MAX_SAFE_INTEGER;
    if (vbIda < vbIdb) {
      return -1;
    } else if (vbIda > vbIdb) {
      return 1;
    }

    // Criterion 3: Index Buffer (ascending id order)
    const ibIda = geomA ? geomA.__gpuib?.id : Number.MAX_SAFE_INTEGER;
    const ibIdb = geomB ? geomB.__gpuib?.id : Number.MAX_SAFE_INTEGER;
    if (ibIda < ibIdb) {
      return -1;
    } else if (ibIda > ibIdb) {
      return 1;
    }

    // If we get here, both frags can be rendered in the same
    // multiDrawCall. If the geomIds are equal as well, we can even
    // combine them in a single instanced drawCall.

    // Criterion 4: geomId (ascending)
    const geomIdA = geomA ? geomA.id : Number.MAX_SAFE_INTEGER;
    const geomIdB = geomB ? geomB.id : Number.MAX_SAFE_INTEGER;
    return geomIdA - geomIdB;
  };

  Array.prototype.sort.call(span, sortBy);

  // Make sure that fragId lookup is updated in ModelUniformUpdater
  renderBatch.onFragOrderChanged();

  return span;
}


/**
 * Check if the RenderBatch is ready to be optimized into MultiDraws.
 * @param {RenderBatch} renderBatch
 * @param {RenderBatchUniforms} renderBatchUniforms
 * @returns {boolean}
 */
export function isReadyToOptimize(renderBatch, renderBatchUniforms) {


  // Wait for ModelIteratorBVH before creating MultiDraws.
  // This would just produce bugs atm. because it tends to change.
  if (renderBatch.creatorIterator instanceof ModelIteratorLinear) {
    return false;
  }

  if (!renderBatch.isComplete) {
    return false;
  }

  // There must be an allocated range for batched uniforms on GPU
  const gpuRange = renderBatchUniforms.getGPUBufferRange(renderBatch);
  if (!gpuRange) {
    return false;
  }

  return true;
}